import React from "react";
import { BreakdownItem } from "../../../types/llm-usage.types";
import { formatCurrency } from "../../../utils/formatters";

interface BreakdownChartsProps {
  byBoundedContext: BreakdownItem[];
  byOperation: BreakdownItem[];
  byModel: BreakdownItem[];
}

const formatPercent = (value: number, total: number): string => {
  if (total === 0) return "0%";
  return `${((value / total) * 100).toFixed(1)}%`;
};

const formatLabel = (label: string): string => {
  // Convert snake_case or UPPER_CASE to Title Case
  return label
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

interface BreakdownSectionProps {
  title: string;
  data: BreakdownItem[];
  testId: string;
}

const BreakdownSection: React.FC<BreakdownSectionProps> = ({
  title,
  data,
  testId,
}) => {
  const total = data.reduce((sum, item) => sum + item.totalCostUsd, 0);

  if (!data.length) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-4">
        <h4 className="text-sm font-semibold text-neutral-900 mb-3">{title}</h4>
        <div className="text-neutral-500 text-sm">Brak danych</div>
      </div>
    );
  }

  // Sort by cost descending
  const sortedData = [...data].sort((a, b) => b.totalCostUsd - a.totalCostUsd);

  return (
    <div
      className="bg-white rounded-lg border border-neutral-200 p-4"
      data-testid={testId}
    >
      <h4 className="text-sm font-semibold text-neutral-900 mb-3">{title}</h4>

      <div className="space-y-3">
        {sortedData.map((item, index) => {
          const percentage = total > 0 ? (item.totalCostUsd / total) * 100 : 0;

          return (
            <div key={item.label} data-testid={`${testId}-item-${index}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-neutral-700">{formatLabel(item.label)}</span>
                <span className="text-neutral-900 font-medium">
                  {formatCurrency(item.totalCostUsd)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded-full transition-all"
                    style={{ width: `${percentage}%` }}
                    role="progressbar"
                    aria-valuenow={percentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${formatLabel(item.label)}: ${formatPercent(item.totalCostUsd, total)} całkowitego kosztu`}
                  />
                </div>
                <span className="text-xs text-neutral-500 w-12 text-right">
                  {formatPercent(item.totalCostUsd, total)}
                </span>
              </div>
              <div className="text-xs text-neutral-400 mt-0.5">
                {item.count.toLocaleString()} rekordów
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-3 border-t border-neutral-100">
        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 font-medium">Suma</span>
          <span className="text-neutral-900 font-semibold">
            {formatCurrency(total)}
          </span>
        </div>
      </div>
    </div>
  );
};

export const BreakdownCharts: React.FC<BreakdownChartsProps> = ({
  byBoundedContext,
  byOperation,
  byModel,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <BreakdownSection
        title="Wg kontekstu"
        data={byBoundedContext}
        testId="chart-by-bc"
      />
      <BreakdownSection
        title="Wg operacji"
        data={byOperation}
        testId="chart-by-operation"
      />
      <BreakdownSection
        title="Wg modelu"
        data={byModel}
        testId="chart-by-model"
      />
    </div>
  );
};

export default BreakdownCharts;
